import {FC, useState, useEffect, useMemo} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Card, Row, Col, Image, OverlayTrigger, Tooltip, Button} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {deleteLegalItem, getAllLegalItems} from './core/_requests'
import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {ListLegalResultRow, ListPendingLegal} from './core/_models'
import {Link} from 'react-router-dom'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../modules/auth/core/AbilityContext'
import {showSwal} from '../../../../ui-kit'
import {MRT_ColumnDef} from 'material-react-table'
import {Box} from '@mui/material'
import ReorderableDataTable from '../../../../ui-kit/ReorderableDataTable/ReorderableDataTable'



const LegalTinaPage: FC<{subject: string}> = ({subject}) => {
  const intl = useIntl()
  const ability = useAbility(AbilityContext)
  const axiosPrivate = useAxiosPrivate()
  const [isLoadingLegal, setIsLoadingLegal] = useState(false)
  const [listLegalTableData, setListLegalTableData] = useState<ListLegalResultRow[]>([])
  const [pendingLegalTina, setPendingLegalTina] = useState<Array<ListPendingLegal> | null>()

  const handleLegalDeletion = (id) => {
    showSwal({
      title: 'Are you sure you want to delete this legal item?',
      icon: 'question',
    }).then((result) => {
      if (!result.isConfirmed) return

      deleteLegalItem(axiosPrivate, id).then((res) => {
        if (res.status === 200) {
          showSwal('Legal item deleted', undefined, 'success').then(() => {
            setListLegalTableData((prev) => prev?.filter((item) => item.id !== id))
          })
        }
      })
    })
  }

  const columns = useMemo<MRT_ColumnDef<ListLegalResultRow>[]>(() => {
    const cols: MRT_ColumnDef<ListLegalResultRow>[] = [
      {
        accessorKey: 'unique_code',
        header: 'Unique Code',
        Cell: ({cell, row}) => {
          return (
            <Box
              component='span'
              sx={{
                paddingLeft: `${row.depth * 2.5}rem`,
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              {row.getCanExpand() ? (
                <i
                  className={`fa-solid cursor-pointer text-muted ${
                    row.getIsExpanded() ? 'fa-sort-down' : 'fa-caret-right'
                  }`}
                  style={{width: '10px', textAlign: 'center'}}
                  onClick={(e) => {
                    e.stopPropagation()
                    row.toggleExpanded()
                  }}
                />
              ) : (
                <span style={{width: '10px'}} />
              )}
              {cell.getValue<string>() ?? '-'}
            </Box>
          )
        },
      },
      {
        accessorKey: 'heading',
        header: 'Heading',
        Cell: ({cell, row}) => {
          return (
            <Box
              component='span'
              sx={{
                paddingLeft: `${row.depth * 2.5}rem`,
              }}
            >
              {cell.getValue<string>() ?? '-'}
            </Box>
          )
        },
      },
      {
        accessorKey: 'id',
        header: 'Actions',
        Cell: ({cell, row}) => {
          const hasChildren = row.original.children && row.original.children.length > 0
          return (
            <div className='d-flex justify-content-center align-items-center'>
              <Link to={`/legal-tina/${encodeURIComponent(row.original.unique_code)}`}>
                <span className='fa fa-eye text-primary'></span>
              </Link>
              {ability.can('delete', subject) ? (
                <OverlayTrigger
                  placement='top'
                  overlay={
                    hasChildren ? (
                      <Tooltip id={`delete-disabled-tooltip-${row.original.unique_code}`}>
                        Cannot delete while there are child contents referring to this item
                      </Tooltip>
                    ) : (
                      <></>
                    )
                  }
                >
                  <span className='d-inline-block'>
                    <Button
                      className='btn btn-sm align-items-center'
                      onClick={() => handleLegalDeletion(row.original.unique_code)}
                      disabled={hasChildren}
                      style={{
                        pointerEvents: hasChildren ? 'none' : 'auto',
                        backgroundColor: 'transparent',
                        border: 'transparent',
                      }}
                    >
                      <span className='fa fa-trash text-danger'></span>
                    </Button>
                  </span>
                </OverlayTrigger>
              ) : (
                ''
              )}
            </div>
          )
        },
      },
    ]
    return cols
  }, [])

  const fetchListLegal = () => {
    setIsLoadingLegal(true)

    getAllLegalItems(axiosPrivate)
      .then((res) => {
        if (res && res.data.table_data) {
          setListLegalTableData(res.data.table_data)
        }
        if (res && res.data.pending_table_data) {
          setPendingLegalTina(res.data.pending_table_data)
        }
      })
      .catch((e) => {
        console.error(e)
        setIsLoadingLegal(false)
      })
      .finally(() => {
        setIsLoadingLegal(false)
      })
  }

  useEffect(() => {
    fetchListLegal()
  }, [])

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        {intl.formatMessage({id: subject})}
      </PageTitle>
      <>
        {pendingLegalTina && pendingLegalTina.length > 0 && (
          <div className='alert-no-result-data'>
            <div className='alert alert-warning d-flex align-items-center justify-content-between p-5'>
              <div className='alert-content d-flex align-items-start'>
                <span className='svg-icon svg-icon-2hx svg-icon-warning me-3'>
                  <i className='fa-solid fa-triangle-exclamation'></i>
                </span>
                <div className='d-flex flex-column'>
                  <h5 className='mb-1'>Review Needed</h5>
                  <span>There are agreements that require review or approval:</span>

                  <ul className='mt-2 mb-0'>
                    {pendingLegalTina.map((legal: ListPendingLegal, index) => (
                      <li key={index}>
                        {ability.can('approve', 'PERM_APPROVE_LEGAL_TINA') ? (
                          <a className='text-link' href={`/legal-tina/review/${legal.id}`}>
                            {legal.heading}
                          </a>
                        ) : (
                          <span className='fw-bold'>{legal.heading}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
        <Row className='mb-5'>
          <Col>
            <Card className='card-custom card-border'>
              <Card.Body className='d-flex justify-content-around align-items-center'>
                <Image src='/media/tina/img-result-documents.jpg' thumbnail width={110} />
                <div className='ms-5 d-flex flex-column justify-content-center'>
                  <h3 className='text-primary'>Legal TINA</h3>
                  <div className='text-gray-700'>
                    Manage and explore the comprehensive list of Legal Issue Areas. This page allows
                    you to view existing legal items, add new entries, update details, or delete
                    obsolete records. You can also easily find specific data using search. Click the
                    detail (<span className='fa fa-eye text-primary'></span>) for more detail about
                    the legal item.
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row className='mb-5'>
          <Col>
            <Card>
              <Card.Header className='card-header-stretch'>
                <Card.Title className='w-100'>
                  <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                    <div className='title d-flex align-items-center'>
                      <span className='fa fa-file-alt px-2'></span>
                      <h3 className='text-gray-700'>List Legal Items</h3>
                    </div>
                    <div className='d-flex gap-2'>
                      {ability.can('create', subject) ? (
                        <Link
                          to={'/legal-tina/add'}
                          className='btn btn-primary d-flex gap-4 align-items-center'
                        >
                          <span className='fa fa-plus'></span>
                          Add
                        </Link>
                      ) : (
                        ''
                      )}
                    </div>
                  </div>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                <ReorderableDataTable
                  data={listLegalTableData}
                  columns={columns}
                  enableRowOrdering={false}
                  isLoading={isLoadingLegal}
                  getRowId={(row) => row.id}
                  getSubRows={(originalRow) => originalRow.children}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </>
    </>
  )
}

export default LegalTinaPage
